import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';

import AlbumFolderPurchaseSelectAccountIdentityContainer from '../../../containers/purchase/album-folder-purchase/AlbumFolderPurchaseSelectAccountIdentityContainer';
import UpdateAccountIdentityContainer from '../../../apps/consumer/containers/UpdateAccountIdentityContainer';
import {
  CREATE_ACCOUNT_IDENTITY_MODAL,
  DISCOVER_PURCHASE_SELECTION_MODAL,
  PURCHASE_IDENTITY_NAV_MODAL,
} from '../../../constants/modals';
import AlbumFolderIdentityNavModal from './AlbumFolderIdentityNavModal';
import ContextualString from '../../contextual/ContextualString';
import {
  getAccountIdentityNamesArray,
  identitiesFilteredOnAccess,
  sortByIdentityRelationship,
} from '../../../util/identity';
import DiscoverAlbumFolderJoinTypeSelectionModal from '../../../containers/discover/DiscoverAlbumFolderJoinTypeSelectionModal';
import {
  ALBUM_IDENTITY_ACCESS,
  IDENTITY_RELATIONSHIP,
} from '../../../constants/identity';
import AlbumFolderPurchaseCreateAccountIdentityContainer, {
  AlbumFolderPurchaseCreateAccountIdentityComponentContainer,
} from '../../../containers/purchase/AlbumFolderPurchaseCreateAccountIdentityContainer';
import IdentityTagSelectionModal, {
  IDENTITY_TAG_SELECTION_MODAL,
} from '../../../apps/temp/views/onboarding/TagSelection/components/IdentityTagSelection/modal';
import {
  isAlbumFolderTagSelectionRequired,
  isAlbumFolderGradeTagSelectionRequired,
} from '../../../apps/temp/views/onboarding/TagSelection/helpers';
import GradeTagSelectionModal, {
  GRADE_TAG_SELECTION_MODAL,
} from '../../../apps/temp/views/onboarding/TagSelection/components/GradeTagSelection/modal';
import OnboardingLayoutScrollableContent from '../../../apps/temp/views/onboarding/OnboardingLayout/components/OnboardingLayoutScrollableContent';
import { getDefaultJoinType } from '../../../util/discover-album-folder';
import { isPrepaidOnlyAlbumFolder } from '../../../util/album-folder';
import AngleRightIcon from '../../icons/AngleRightIcon';
import { getAsset } from '../../../util/assets';

class AlbumFolderPurchaseSelectAccountIdentities extends Component {
  static propTypes = {
    albumFolder: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    identity: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      selectedAlbums: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.name,
        }),
      ),
    }),
    identities: PropTypes.arrayOf(PropTypes.object),
    purchasedIdentities: PropTypes.arrayOf(PropTypes.object),
    selectedIdentities: PropTypes.arrayOf(PropTypes.object),
    openModal: PropTypes.func.isRequired,
    closeModal: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    onSelectIdentity: PropTypes.func,
    onDeselectIdentity: PropTypes.func,
    subscribedAlbumFolderIdentities: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        album_folder: PropTypes.shape({
          id: PropTypes.string,
        }),
        identity: PropTypes.shape({
          id: PropTypes.string,
        }),
      }),
    ),
    allowsJoinTypeSelection: PropTypes.bool.isRequired,
    history: PropTypes.shape({
      replace: PropTypes.func.isRequired,
    }).isRequired,
    onSelectAlbumCustomPrint: PropTypes.func.isRequired,
    onDeselectAlbumCustomPrint: PropTypes.func.isRequired,
    identityFilter: PropTypes.oneOf(Object.values(IDENTITY_RELATIONSHIP)),
  };

  onAddFace = event => {
    if (event) {
      event.preventDefault();
    }
    this.props.openModal(CREATE_ACCOUNT_IDENTITY_MODAL);
  };

  onIdentityUpdatedOrCreated = identity => {
    const {
      albumFolder,
      onSelectIdentity,
      setActiveIdentity,
      openModal,
      history,
    } = this.props;
    onSelectIdentity(
      {
        ...identity,
        albumFolders: [albumFolder],
        selectedAlbums: [],
      },
      getDefaultJoinType(albumFolder),
    );
    setActiveIdentity(identity);

    const onSelectionCompleted = () => {
      // Use this so we can easily add SUBTYPES to show the modal to, if needed.
      const identityModalSubtypes = [];
      const isPrimaryIdentityOnly =
        albumFolder.album_identity_access ===
        ALBUM_IDENTITY_ACCESS.PRIMARY_ONLY;

      if (
        identityModalSubtypes.includes(albumFolder.album_subtype) &&
        !isPrimaryIdentityOnly
      ) {
        openModal(PURCHASE_IDENTITY_NAV_MODAL);
      } else if (isPrepaidOnlyAlbumFolder(albumFolder)) {
        history.replace(
          `/purchase/album-folder/${albumFolder.id}/pre-purchase`,
        );
      } else {
        history.replace(
          `/purchase/album-folder/${albumFolder.id}/purchase-summary`,
        );
      }
    };

    if (this.allowsJoinTypeSelection()) {
      openModal(DISCOVER_PURCHASE_SELECTION_MODAL);
      return;
    }

    if (isAlbumFolderGradeTagSelectionRequired(albumFolder)) {
      openModal(GRADE_TAG_SELECTION_MODAL, {
        onComplete: () => {
          if (isAlbumFolderTagSelectionRequired(albumFolder)) {
            openModal(IDENTITY_TAG_SELECTION_MODAL, {
              onComplete: onSelectionCompleted,
            });

            return;
          }

          onSelectionCompleted();
        },
      });

      return;
    }

    if (isAlbumFolderTagSelectionRequired(albumFolder)) {
      openModal(IDENTITY_TAG_SELECTION_MODAL, {
        onComplete: onSelectionCompleted,
      });

      return;
    }

    onSelectionCompleted();
  };

  onComplete = () => {
    const { history, albumFolder } = this.props;

    if (isPrepaidOnlyAlbumFolder(albumFolder)) {
      history.replace(`/purchase/album-folder/${albumFolder.id}/pre-purchase`);
    }

    const nextStepLink = `/purchase/album-folder/${albumFolder.id}/purchase-summary`;

    history.replace(nextStepLink);
  };

  allowsJoinTypeSelection = () => {
    const { albumFolder, allowsJoinTypeSelection } = this.props;

    // We will skip selecting join type if we have a default. This is because we will be upselling the DPP later down the line.
    if (getDefaultJoinType(albumFolder)) {
      return false;
    }

    // This is initially set in `SetAlbumFolderJoinPreferences`
    // via the redux action (based on album folder config and other params)
    // when the onboarding initializes and then can be set again
    // by editing the identity during the checkout summary.
    return allowsJoinTypeSelection;
  };

  renderIdentity(identity) {
    const {
      selectedIdentities,
      purchasedIdentities,
      albumFolder,
      subscribedAlbumFolderIdentities,
      history,
      identityFilter,
    } = this.props;

    const isSelected = !!(selectedIdentities || []).find(
      selectedIdentity => selectedIdentity.id === identity.id,
    );
    const isPurchased = !!(purchasedIdentities || []).find(
      purchasedIdentity => purchasedIdentity.id === identity.id,
    );
    const isJoined = !!(subscribedAlbumFolderIdentities || []).find(
      subscribedItem => subscribedItem.identity.id === identity.id,
    );

    return (
      <AlbumFolderPurchaseSelectAccountIdentityContainer
        key={identity.id}
        identity={identity}
        isSelected={isSelected}
        isPurchased={isPurchased}
        albumFolder={albumFolder}
        isJoined={isJoined}
        history={history}
        allowsJoinTypeSelection={this.allowsJoinTypeSelection()}
        identityFilter={identityFilter}
      />
    );
  }

  render() {
    const {
      identities,
      loading,
      onSelectIdentity,
      setActiveIdentity,
      albumFolder,
      openModal,
      closeModal,
      identity,
      onSelectAlbumCustomPrint,
      onDeselectAlbumCustomPrint,
      onDeselectIdentity,
      history,
      identityFilter,
    } = this.props;

    const sortedIdentities = sortByIdentityRelationship(identities ?? []);
    const filteredIdentities = (() => {
      if (!albumFolder) {
        return [];
      }

      if (!identityFilter) {
        return identitiesFilteredOnAccess(
          sortedIdentities,
          albumFolder.album_identity_access,
        );
      }

      return sortedIdentities.filter(i => i.relationship === identityFilter);
    })();

    return (
      <Fragment>
        {loading || !albumFolder ? (
          <div className="d-block text-center">
            <span className="spinner" />
          </div>
        ) : (
          <Fragment>
            {filteredIdentities?.length === 0 ? (
              <div className="w-100">
                <AlbumFolderPurchaseCreateAccountIdentityComponentContainer
                  includeTitle={false}
                  onIdentityCreated={this.onIdentityUpdatedOrCreated}
                  stringContext={albumFolder}
                  identityNames={getAccountIdentityNamesArray(sortedIdentities)}
                  requiresSelfieChecklistVerification
                  noCancelOption
                />
              </div>
            ) : (
              <OnboardingLayoutScrollableContent className="w-100">
                <div className="identity-list-container">
                  {(filteredIdentities || []).map(identityItem =>
                    this.renderIdentity(identityItem),
                  )}

                  {get(albumFolder, 'album_identity_access') !==
                    ALBUM_IDENTITY_ACCESS.PRIMARY_ONLY && (
                    <button
                      onClick={this.onAddFace}
                      className="new-identity-container"
                    >
                      <img
                        src={getAsset('/icon-add-face-blue.svg')}
                        alt=""
                        className="mr-2"
                        width="52"
                        height="52"
                      />
                      <div className="mb-0 d-flex justify-content-between align-items-center">
                        <div className="text-content text-dark">
                          <ContextualString
                            id="consumerAddNewIdentity"
                            subtype={albumFolder.album_subtype}
                            defaultMessage="Find photos of a family member"
                          />
                        </div>
                        <div>
                          <AngleRightIcon
                            color="#1983fb"
                            height="18px"
                            className="ml-1"
                          />
                        </div>
                      </div>
                    </button>
                  )}
                </div>
              </OnboardingLayoutScrollableContent>
            )}
          </Fragment>
        )}

        <AlbumFolderPurchaseCreateAccountIdentityContainer
          onIdentityCreated={this.onIdentityUpdatedOrCreated}
          stringContext={albumFolder}
          identityNames={getAccountIdentityNamesArray(sortedIdentities)}
          requiresSelfieChecklistVerification
        />
        <UpdateAccountIdentityContainer
          requiresSelfieChecklistVerification
          stringContext={albumFolder}
          onIdentityUpdated={this.onIdentityUpdatedOrCreated}
        />
        <DiscoverAlbumFolderJoinTypeSelectionModal
          albumFolder={albumFolder}
          identity={identity}
          onAfterSelect={() => {
            const onSelectionCompleted = () => {
              // Use this so we can easily add SUBTYPES to show the modal to, if needed.
              const identityModalSubtypes = [];
              const isPrimaryIdentityOnly =
                albumFolder.album_identity_access ===
                ALBUM_IDENTITY_ACCESS.PRIMARY_ONLY;

              if (
                identityModalSubtypes.includes(albumFolder.album_subtype) &&
                !isPrimaryIdentityOnly
              ) {
                openModal(PURCHASE_IDENTITY_NAV_MODAL);
              } else if (isPrepaidOnlyAlbumFolder(albumFolder)) {
                history.replace(
                  `/purchase/album-folder/${albumFolder.id}/pre-purchase`,
                );
              } else {
                history.replace(
                  `/purchase/album-folder/${albumFolder.id}/purchase-summary`,
                );
              }
            };

            if (isAlbumFolderGradeTagSelectionRequired(albumFolder)) {
              openModal(GRADE_TAG_SELECTION_MODAL, {
                onComplete: () => {
                  if (isAlbumFolderTagSelectionRequired(albumFolder)) {
                    openModal(IDENTITY_TAG_SELECTION_MODAL, {
                      onComplete: onSelectionCompleted,
                    });

                    return;
                  }

                  onSelectionCompleted();
                },
              });

              return;
            }

            if (isAlbumFolderTagSelectionRequired(albumFolder)) {
              openModal(IDENTITY_TAG_SELECTION_MODAL, {
                onComplete: onSelectionCompleted,
              });
              return;
            }

            onSelectionCompleted();
          }}
          onSelectIdentity={(selectedIdentity, joinType) => {
            onSelectIdentity(selectedIdentity, joinType);
            setActiveIdentity(selectedIdentity);
          }}
          onSelectAlbumCustomPrint={onSelectAlbumCustomPrint}
          onDeselectAlbumCustomPrint={onDeselectAlbumCustomPrint}
        />
        {identity && (
          <AlbumFolderIdentityNavModal
            identity={identity}
            albumFolder={albumFolder}
            addNewIdentity={() => {
              closeModal(PURCHASE_IDENTITY_NAV_MODAL);
              this.onAddFace();
            }}
            albumFolderId={albumFolder.id}
            closeModal={closeModal}
            identities={identities}
            onComplete={this.onComplete}
          />
        )}

        <IdentityTagSelectionModal
          onDeselectIdentity={onDeselectIdentity}
          identity={identity}
          albumFolder={albumFolder}
        />
        <GradeTagSelectionModal identity={identity} albumFolder={albumFolder} />
      </Fragment>
    );
  }
}

export default AlbumFolderPurchaseSelectAccountIdentities;
